/**
 * SPA boot for BYO custom domains. Rendered by App.tsx in place of the
 * homepage when isCustomDomain(host). Resolves via public
 * `GET /venues/by-domain?hostname=<host>`: 200 → Navigate to
 * `/venues/<slug>`; 404 → "Not configured" splash.
 */

import { useQuery } from '@tanstack/react-query';
import { Navigate } from 'react-router-dom';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import { useTranslation } from 'react-i18next';
import venuesService, { VenueByDomainResult } from '../../services/venuesService';
import { colors } from '../../theme/theme';

interface Props {
  /** Pass `window.location.host` from the caller (App.tsx) so tests can inject. */
  host: string;
}

export default function BYODomainBoot({ host }: Props) {

  const { data, isLoading, isError } = useQuery<VenueByDomainResult>({
    queryKey: ['venues', 'by-domain', host],
    queryFn: () => venuesService.lookupByDomain(host.split(':')[0]),
    retry: 0,
    // Cache for 5 min — the SPA may re-render during the session.
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: colors.background.default,
        }}
      >
        <CircularProgress sx={{ color: colors.primary.main }} />
      </Box>
    );
  }

  if (isError || !data) {
    return <CustomDomainNotConfigured host={host} />;
  }

  return <Navigate to={`/venues/${data.slug}`} replace />;
}


function CustomDomainNotConfigured({ host }: { host: string }) {
  const { t } = useTranslation();
  const cleanHost = host.split(':')[0];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        bgcolor: colors.background.default,
        color: '#fff',
        p: 4,
        textAlign: 'center',
      }}
    >
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
        {t('byo_domain.not_configured_title')}
      </Typography>
      <Typography variant="body1" sx={{ color: colors.neutral[500], maxWidth: 540, mb: 4 }}>
        {t('byo_domain.not_configured_body', { host: cleanHost })}
      </Typography>
      <Button
        variant="contained"
        href="https://coffeemates.biz"
        sx={{
          background: `linear-gradient(135deg, ${colors.primary.main}, ${colors.primary.dark})`,
          color: '#fff',
          fontWeight: 600,
          textTransform: 'none',
          px: 4,
          py: 1.25,
          '&:hover': {
            background: `linear-gradient(135deg, ${colors.primary.dark}, ${colors.primary.main})`,
          },
        }}
      >
        {t('byo_domain.visit_coffeemates')}
      </Button>
    </Box>
  );
}
